import {
  CloseButton,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useLimitRequests } from "../../../api/hooks/limitRequest/useLimitRequests.ts";
import { useUpdateLimitRequestInline } from "../../../api/hooks/limitRequest/useUpdateLimitRequestInline.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  OrderBy,
  StringFilterMode,
  UpdateLimitRequestInlineInput,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { CustomerOption } from "../../../types/customer.ts";
import { limitRequestTypeOptions } from "../../../types/limitRequest/limitRequest.ts";
import { UserOption } from "../../../types/user.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../../utils/filter.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { getUserOptions } from "../../../utils/user.ts";
import { CUStatusOptions } from "../constants.ts";
import { getLimitRequestApiSort } from "../utils.ts";
import { useOngoingCUTableData } from "./hooks/useOngoingCUTableData.tsx";

interface Props {
  companies: CustomerOption[] | undefined;
  assignees: UserOption[] | undefined;
}

const OngoingCUTable = ({ companies, assignees }: Props) => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const apiFilter: LimitRequestFilter = {
    ...filter,
    requestStatus: filter?.requestStatus,
    limitName: debouncedSearch
      ? {
          contains: debouncedSearch,
          mode: StringFilterMode.Insensitive,
        }
      : undefined,
  };
  const [{ data: response, fetching, error }, refreshData] = useLimitRequests({
    filter: apiFilter,
    orderBy,
    page,
  });
  const data = response?.limitRequests?.data;

  const pageCount = getPagesCount(response?.limitRequests?.total);

  const [, updateLimitRequestCategory] = useMutationNotificationWrapper(
    useUpdateLimitRequestInline(),
    {
      success: { message: "Limit Request has been successfully updated." },
    }
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getLimitRequestApiSort(sort));
    },
    [setOrderBy]
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter(getLimitRequestApiFilter({ [key]: value }));
  }, []);

  const handleEdit = useCallback(
    (id: number, input: UpdateLimitRequestInlineInput) => {
      updateLimitRequestCategory({ id, input }).then(refreshData);
    },
    [refreshData, updateLimitRequestCategory]
  );

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const [columns, rows] = useOngoingCUTableData({ data, onEdit: handleEdit });

  if (error) {
    notifications.clean();
    notifications.show({
      message:
        "Something went wrong while trying to fetch Limit Requests data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={20}>
      <SectionTitle>Ongoing CU</SectionTitle>
      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 2 }]}
        spacing={20}
        verticalSpacing={12}
        pb={8}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Company name"
          placeholder="Select Company name"
          data={getCompanyOptions(companies)}
          onChange={(value) => handleFilterChange("companyName", value)}
        />
        <Select
          size="m"
          clearable
          searchable
          label="CU Assigned"
          placeholder="Select CU Assigned"
          data={getUserOptions(assignees)}
          onChange={(value) =>
            handleFilterChange("cuAssignedId", Number(value))
          }
        />
        <Select
          size="m"
          clearable
          searchable
          label="CU Status"
          placeholder="Select CU Status"
          data={CUStatusOptions}
          onChange={(value) => handleFilterChange("requestStatus", value)}
        />
        <Select
          size="m"
          clearable
          label="Limit Type"
          placeholder="Select Limit Type"
          data={limitRequestTypeOptions}
          onChange={(value) => handleFilterChange("limitRequestType", value)}
        />
        <TextInput
          size="m"
          label="Limit Name"
          placeholder="Enter Limit Name"
          rightSection={
            <CloseButton
              aria-label="Clear input"
              variant="transparent"
              size="md"
              mr="5px"
              style={{
                display: search ? undefined : "none",
              }}
              onClick={() => setSearch("")}
            />
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "createdAt",
          direction: SortDirection.asc,
        }}
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onRowClick={handleRowClik}
        onSortChange={handleSortChange}
      />
    </Flex>
  );
};

export default OngoingCUTable;
